/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Card,
  Grid,
  TextField,
  IconButton,
  Alert,
  Snackbar,
  Chip,
} from '@mui/material';
import { Add, ArrowBack, Delete, Edit, Save } from '@mui/icons-material';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import { useAuth } from 'auth/Auth';
import FieldEditorDialog from 'components/SettingsComponents/FieldEditorDialog';
import DataLoadingSpinner from 'components/Spinners/DataLoadingSpinner';
import {
  MAX_FIELDS,
  DEFAULT_SECTION,
  validateObjectDefinition,
  countCustomFields,
  buildPayload,
} from 'components/SettingsComponents/customObjectsFieldTypes';
import { useTheme } from "@mui/material/styles";
import PageHeader from "components/v2/PageHeader";

export default function CustomObjectDesigner() {
  const isV2 = useTheme()?.pearlUi === "v2";
  const { axiosInstance } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(null);
  const [editingObject, setEditingObject] = useState(null);
  const [customObjects, setCustomObjects] = useState([]);

  const [fieldDialogOpen, setFieldDialogOpen] = useState(false);
  const [fieldDraft, setFieldDraft] = useState(null);
  const [fieldIndex, setFieldIndex] = useState({ section: 0, index: -1 });
  const [isNewField, setIsNewField] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    (async () => {
      try {
        const listRes = await axiosInstance.get('/custom-objects');
        const objects = listRes.data || [];
        setCustomObjects(objects);

        if (isEditMode) {
          const obj = objects.find((o) => String(o.id) === String(id));
          if (!obj) {
            setSnackbar({ open: true, message: 'Custom object not found', severity: 'error' });
            navigate('/settings/custom-objects');
            return;
          }
          setEditingObject(obj);
          setDraft({
            name: obj.name,
            pluralName: obj.pluralName,
            description: obj.description || '',
            sections: (obj.sections || [])
              .filter((s) => !s.is_system && s.section_id !== 'audit')
              .map((s) => ({
                id: s.section_id || String(s.id),
                name: s.name,
                isSystem: false,
                fields: (s.fields || []).filter((f) => !f.readOnly).map((f) => ({ ...f, id: f.field_id || f.id })),
              })),
          });
        } else {
          setDraft({ name: '', pluralName: '', description: '', sections: [DEFAULT_SECTION()] });
        }
      } catch (error) {
        setSnackbar({ open: true, message: 'Failed to load custom object', severity: 'error' });
      } finally {
        setLoading(false);
      }
    })();
  }, [axiosInstance, id, isEditMode, navigate]);

  const goBack = () => navigate('/settings/custom-objects');

  const saveObject = async () => {
    const errors = validateObjectDefinition(draft);
    if (errors.length) {
      setSnackbar({ open: true, message: errors[0], severity: 'error' });
      return;
    }
    setSaving(true);
    try {
      const payload = buildPayload(draft);
      if (editingObject?.id) {
        await axiosInstance.put(`/custom-objects/${editingObject.id}`, payload);
      } else {
        await axiosInstance.post('/custom-objects', payload);
      }
      setSnackbar({ open: true, message: 'Custom object saved', severity: 'success' });
      window.dispatchEvent(new CustomEvent('sidenavPreferencesUpdated'));
      goBack();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = Array.isArray(detail)
        ? detail.map((d) => d.msg || JSON.stringify(d)).join(', ')
        : (detail || 'Failed to save custom object');
      setSnackbar({ open: true, message, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const addField = (sectionIdx) => {
    setFieldIndex({ section: sectionIdx, index: -1 });
    setIsNewField(true);
    setFieldDraft(null);
    setFieldDialogOpen(true);
  };

  const editField = (sectionIdx, index, field) => {
    setFieldIndex({ section: sectionIdx, index });
    setIsNewField(false);
    setFieldDraft(field);
    setFieldDialogOpen(true);
  };

  const saveField = (field) => {
    setDraft((prev) => {
      const sections = [...prev.sections];
      const section = { ...sections[fieldIndex.section], fields: [...(sections[fieldIndex.section].fields || [])] };
      if (fieldIndex.index >= 0) section.fields[fieldIndex.index] = field;
      else section.fields.push(field);
      sections[fieldIndex.section] = section;
      return { ...prev, sections };
    });
  };

  const addSection = () => {
    setDraft((prev) => ({
      ...prev,
      sections: [
        ...prev.sections,
        {
          id: `section_${Date.now()}`,
          name: `Section ${(prev.sections?.length || 0) + 1}`,
          isSystem: false,
          fields: [],
        },
      ],
    }));
  };

  const removeSection = (sectionIdx) => {
    setDraft((prev) => {
      if ((prev.sections || []).length <= 1) {
        setSnackbar({ open: true, message: 'At least one section is required', severity: 'warning' });
        return prev;
      }
      return { ...prev, sections: prev.sections.filter((_, i) => i !== sectionIdx) };
    });
  };

  if (loading || !draft) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <DataLoadingSpinner
          entityType="default"
          title="Loading Designer"
          message="Please wait while we open the custom object designer..."
        />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={isV2 ? 0 : 4} pb={6}>
        {isV2 ? (
          <PageHeader
            title={isEditMode ? 'Edit Custom Object' : 'New Custom Object'}
            summary={isEditMode ? 'Update the name, description, and fields for this object.' : 'Define a new module for your CRM, similar to Leads or Accounts.'}
          />
        ) : (
          <MDBox display="flex" alignItems="center" gap={1} mb={3}>
            <IconButton onClick={goBack}>
              <ArrowBack />
            </IconButton>
            <MDBox>
              <MDTypography variant="h4" fontWeight="medium">
                {isEditMode ? 'Edit Custom Object' : 'New Custom Object'}
              </MDTypography>
              <MDTypography variant="body2" color="text">
                {isEditMode
                  ? 'Update the name, description, and fields for this object.'
                  : 'Define a new module for your CRM, similar to Leads or Accounts.'}
              </MDTypography>
            </MDBox>
          </MDBox>
        )}

        <Card sx={{ p: 3, mb: 3 }}>
          <MDTypography variant="h6" fontWeight="medium" mb={2}>
            Object details
          </MDTypography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Object Name (singular)"
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                margin="dense"
                helperText="e.g. Property, Vehicle, Vendor"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Plural Name"
                value={draft.pluralName}
                onChange={(e) => setDraft({ ...draft, pluralName: e.target.value })}
                margin="dense"
                helperText="e.g. Properties, Vehicles, Vendors"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Description"
                value={draft.description}
                onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                margin="dense"
                helperText="Optional. Shown to users in Settings and on the module hub."
              />
            </Grid>
            <Grid item xs={12}>
              <Chip label={`${countCustomFields(draft.sections)} / ${MAX_FIELDS} fields`} color="info" size="small" />
            </Grid>
          </Grid>
        </Card>

        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <MDBox>
            <MDTypography variant="h6" fontWeight="medium">
              Form sections
            </MDTypography>
            <MDTypography variant="caption" color="text" display="block">
              Each section groups fields on the form. Use <strong>Add field</strong> inside a section, and{' '}
              <strong>Add section</strong> only when you need a new group.
            </MDTypography>
          </MDBox>
          <MDButton color="secondary" variant="outlined" onClick={addSection}>
            <Add sx={{ mr: 0.5 }} /> Add section
          </MDButton>
        </MDBox>

        <Grid container spacing={2}>
          {(draft.sections || []).map((section, sectionIdx) => (
            <Grid item xs={12} md={6} key={section.id || `section-${sectionIdx}`}>
              <Card
                variant="outlined"
                sx={{
                  p: 2,
                  height: '100%',
                  bgcolor: 'background.paper',
                  borderColor: 'divider',
                }}
              >
                <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" gap={1} mb={1.5}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Section name"
                    value={section.name}
                    onChange={(e) => {
                      const sections = [...draft.sections];
                      sections[sectionIdx] = { ...section, name: e.target.value };
                      setDraft({ ...draft, sections });
                    }}
                  />
                  <IconButton
                    size="small"
                    color="error"
                    title="Remove section"
                    onClick={() => removeSection(sectionIdx)}
                    disabled={(draft.sections || []).length <= 1}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </MDBox>

                <MDTypography variant="caption" color="text" display="block" mb={1}>
                  Fields in this section ({(section.fields || []).length})
                </MDTypography>

                {(section.fields || []).length === 0 && (
                  <Alert severity="info" sx={{ mb: 1.5, py: 0.5 }}>
                    No fields yet. Click &quot;Add field&quot; below to add labels to this section.
                  </Alert>
                )}

                {(section.fields || []).map((field, idx) => (
                  <MDBox
                    key={field.id || `${section.id}-field-${idx}`}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    py={1}
                    px={1.5}
                    sx={{ bgcolor: 'grey.100', borderRadius: 1, mb: 1 }}
                  >
                    <MDBox>
                      <MDTypography variant="button">{field.label || '(untitled)'}</MDTypography>
                      <MDTypography variant="caption" display="block" color="text">
                        {field.type} · {field.name || '—'}
                        {field.helpText ? ` · ${field.helpText}` : ''}
                      </MDTypography>
                    </MDBox>
                    <Box>
                      <IconButton size="small" onClick={() => editField(sectionIdx, idx, field)} title="Edit field">
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        title="Delete field"
                        onClick={() => {
                          const sections = [...draft.sections];
                          sections[sectionIdx] = {
                            ...section,
                            fields: section.fields.filter((_, i) => i !== idx),
                          };
                          setDraft({ ...draft, sections });
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  </MDBox>
                ))}

                <MDButton
                  size="small"
                  color="info"
                  variant="outlined"
                  onClick={() => addField(sectionIdx)}
                  disabled={countCustomFields(draft.sections) >= MAX_FIELDS}
                  sx={{ mt: 0.5 }}
                >
                  <Add fontSize="small" sx={{ mr: 0.5 }} /> Add field to this section
                </MDButton>
              </Card>
            </Grid>
          ))}
        </Grid>

        <MDBox display="flex" gap={1} justifyContent="flex-end" mt={4}>
          <MDButton color="secondary" onClick={goBack}>
            Cancel
          </MDButton>
          <MDButton color="info" variant="gradient" onClick={saveObject} disabled={saving}>
            <Save sx={{ mr: 0.5 }} fontSize="small" />
            {saving ? 'Saving...' : 'Save Custom Object'}
          </MDButton>
        </MDBox>
      </MDBox>

      <FieldEditorDialog
        open={fieldDialogOpen}
        field={fieldDraft}
        isNew={isNewField}
        onClose={() => setFieldDialogOpen(false)}
        onSave={saveField}
        customObjects={customObjects}
      />

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </DashboardLayout>
  );
}
